import { useEffect, useRef, useState } from 'react';

function playCelebrationSound() {
  try {
    const audio = new Audio('/pop.mp3');
    audio.volume = 0.8;
    audio.play().catch(() => {});
  } catch {}
}


export function useFreeDeliveryUnlock(cartTotal, freeShippingAbove) {
  const prevTotal = useRef(cartTotal);
  const hasUnlocked = useRef(false);
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    if (!freeShippingAbove || freeShippingAbove <= 0) return;

    // Total dropped below threshold → reset so next cross triggers again
    if (cartTotal < freeShippingAbove) {
      hasUnlocked.current = false;
    }

    // Crossed threshold for the first time (since last drop below)
    if (
      !hasUnlocked.current &&
      prevTotal.current < freeShippingAbove &&
      cartTotal >= freeShippingAbove
    ) {
      hasUnlocked.current = true;
      setCelebrate(true);
      const t = setTimeout(() => setCelebrate(false), 5000);
      prevTotal.current = cartTotal;
      return () => clearTimeout(t);
    }

    prevTotal.current = cartTotal;
  }, [cartTotal, freeShippingAbove]);

  return [celebrate, () => setCelebrate(false)];
}

const COLORS = [
  '#F5B400', '#FF6B6B', '#4ECDC4', '#45B7D1',
  '#96CEB4', '#FF8B94', '#A8E6CF', '#FFD93D',
  '#6C5CE7', '#FD79A8', '#00B894', '#E17055',
];

function ConfettiCanvas() {
  const canvasRef = useRef(null);
  const pieces = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Create confetti pieces
    pieces.current = Array.from({ length: 120 }, () => {
      const isString = Math.random() < 0.3;
      return {
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * 200,
        w: isString ? 2 + Math.random() * 2 : 6 + Math.random() * 10,
        h: isString ? 15 + Math.random() * 25 : 5 + Math.random() * 8,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 8,
        vx: (Math.random() - 0.5) * 3,
        vy: 2 + Math.random() * 4,
        isString,
        wave: Math.random() * Math.PI * 2,
        waveSpeed: 0.05 + Math.random() * 0.05,
        waveAmp: 1 + Math.random() * 2,
        opacity: 1,
      };
    });

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      pieces.current.forEach(p => {
        p.y += p.vy;
        p.x += p.vx + Math.sin(p.wave) * p.waveAmp;
        p.wave += p.waveSpeed;
        p.rotation += p.rotationSpeed;
        if (p.y > canvas.height * 0.7) {
          p.opacity = Math.max(0, p.opacity - 0.02);
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;

        if (p.isString) {
          // Draw wavy string/ribbon
          ctx.beginPath();
          ctx.moveTo(0, -p.h / 2);
          for (let i = 0; i <= p.h; i += 2) {
            ctx.lineTo(Math.sin(i * 0.4) * p.w * 2, -p.h / 2 + i);
          }
          ctx.lineWidth = p.w;
          ctx.strokeStyle = p.color;
          ctx.stroke();
        } else {
          // Draw rectangle confetti piece
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }

        ctx.restore();
      });

      rafRef.current = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[998]"
      style={{ width: '100vw', height: '100vh' }}
    />
  );
}

export default function FreeDeliveryCelebration({ onClose }) {
  useEffect(() => {
    playCelebrationSound();
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <>
      <ConfettiCanvas />

      <div className="fixed inset-0 z-[999] flex items-center justify-center" onClick={onClose}>
        <div className="absolute inset-0 bg-black/40" />

        <div
          className="relative z-10 bg-white rounded-3xl px-10 py-8 text-center shadow-2xl max-w-xs mx-4"
          style={{ animation: 'pop-in 0.4s cubic-bezier(0.175,0.885,0.32,1.275) forwards' }}
          onClick={e => e.stopPropagation()}
        >
          <div className="text-6xl mb-3" style={{ animation: 'bounce-emoji 0.5s ease infinite alternate' }}>
            🎉
          </div>
          <h2 className="text-xl font-black text-brand-dark mb-1">FREE Delivery Unlocked!</h2>
          <p className="text-sm text-gray-500 mb-5">You've earned free delivery on this order!</p>
          <button
            onClick={onClose}
            className="bg-brand-yellow text-brand-dark font-bold px-8 py-2.5 rounded-full text-sm hover:opacity-90 transition-opacity"
          >
            Awesome! 🎊
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pop-in {
          0%   { transform: scale(0.4); opacity: 0; }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes bounce-emoji {
          from { transform: scale(1)   rotate(-8deg); }
          to   { transform: scale(1.3) rotate(8deg);  }
        }
      `}</style>
    </>
  );
}
